import React, { useState } from 'react';
import { CampaignAudience, CampaignChannel, CampaignLang, CampaignText, createCampaign, MarketingCampaign } from '../../../api/services/marketingService';
import { AUDIENCE_LABEL, emptyContent, inputClass, KIND_LABEL, LANGS, SELECTABLE_AUDIENCES } from './shared';

/**
 * Nouvelle campagne écrite à la main (push et/ou e-mail, trois langues). Elle
 * est créée « ouverte » : une autre personne devra l'approuver.
 */
interface ComposerProps {
  token: string;
  onCancel: () => void;
  onCreated: (campaign: MarketingCampaign) => void;
}

const CampaignComposer = ({ token, onCancel, onCreated }: ComposerProps) => {
  const [lang, setLang] = useState<CampaignLang>('fr');
  const [content, setContent] = useState<Record<CampaignLang, CampaignText>>(emptyContent);
  const [kind, setKind] = useState('custom');
  const [audience, setAudience] = useState<CampaignAudience>('all');
  const [channels, setChannels] = useState<CampaignChannel[]>(['push', 'email']);
  const [link, setLink] = useState('dingo://map');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const text = content[lang];
  const dir = LANGS.find((l) => l.code === lang)?.dir || 'ltr';
  const setField = (field: keyof CampaignText, value: string) =>
    setContent((current) => ({ ...current, [lang]: { ...current[lang], [field]: value } }));

  const canSave = content.fr.title.trim().length > 0 && content.fr.body.trim().length > 0 && channels.length > 0 && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const campaign = await createCampaign(token, { kind, content, channels, audience, link: link.trim() || null, note: note.trim() || null });
      onCreated(campaign);
    } catch (err: any) {
      setError(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[18px] font-semibold text-gray-900">Nouvelle campagne</h3>
          <p className="text-[12.5px] text-gray-500">Le français est obligatoire ; une langue laissée vide reprend le français.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900">Annuler</button>
          <button type="button" disabled={!canSave} onClick={submit} className="px-3.5 py-2 text-sm font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Enregistrement…' : 'Mettre en attente'}
          </button>
        </div>
      </div>

      {error && <div className="mt-4 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-700">{error}</div>}

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-5">
        <div>
          <div className="inline-flex rounded-xl border border-gray-200 p-0.5 text-[13px] font-medium">
            {LANGS.map((l) => (
              <button key={l.code} type="button" onClick={() => setLang(l.code)} className={`rounded-lg px-3.5 py-1.5 transition-colors ${lang === l.code ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {l.label}{l.code !== 'fr' && !content[l.code].title && <span className="ml-1 text-[10px] opacity-60">(fr)</span>}
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-4" dir={dir}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre du push</label>
              <input type="text" value={text.title} maxLength={80} onChange={(e) => setField('title', e.target.value)} placeholder="Ex : Ce week-end, sortez de Tunis" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message du push</label>
              <textarea value={text.body} maxLength={240} rows={3} onChange={(e) => setField('body', e.target.value)} className={`${inputClass} resize-none`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Objet de l’e-mail <span className="text-gray-400 font-normal">(sinon : le titre)</span></label>
              <input type="text" value={text.subject} maxLength={120} onChange={(e) => setField('subject', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Corps de l’e-mail (HTML) <span className="text-gray-400 font-normal">(sinon : le message du push)</span></label>
              <textarea value={text.html} rows={7} onChange={(e) => setField('html', e.target.value)} placeholder="<p>…</p>" className={`${inputClass} font-mono text-[12px]`} />
            </div>
          </div>
        </div>
        <aside className="rounded-xl border border-gray-100 p-3 space-y-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Genre</p>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className={inputClass}>
              {Object.entries(KIND_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Canaux</p>
            {(['push', 'email'] as CampaignChannel[]).map((channel) => (
              <label key={channel} className="flex items-center gap-2 py-0.5 text-sm text-gray-700">
                <input type="checkbox" checked={channels.includes(channel)} onChange={() => setChannels((c) => (c.includes(channel) ? c.filter((x) => x !== channel) : [...c, channel]))} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                {channel === 'push' ? 'Notification push' : 'E-mail'}
              </label>
            ))}
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Audience</p>
            <select value={audience} onChange={(e) => setAudience(e.target.value as CampaignAudience)} className={inputClass}>
              {SELECTABLE_AUDIENCES.map((a) => <option key={a} value={a}>{AUDIENCE_LABEL[a]}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Lien ouvert au tap</p>
            <input type="text" value={link} onChange={(e) => setLink(e.target.value)} className={`${inputClass} font-mono text-[12px]`} />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Note interne</p>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Pourquoi cette campagne" className={inputClass} />
          </div>
        </aside>
      </div>
    </div>
  );
};


export default CampaignComposer;
